import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Pricing from "../pricing/Pricing";
import {
  PAYWALL_PLAN_OPTIONS,
  PAYWALL_TOOL_LABELS,
  getPaywallFeature,
} from "../../../data/paywallFeatures";
import { YEARLY_SAVE_PERCENT, yearlyFromMonthly } from "../../../data/pricingModal";
import "./FeaturePaywallModal.css";

const CrownIcon = () => (
  <svg
    className="fpm-crown"
    viewBox="0 0 16 16"
    width="16"
    height="16"
    aria-hidden="true"
  >
    <path
      d="M2.5 12.5h11l-1.2-7.2L8.8 8.2 8 4.5l-.8 3.7-3.5-2.9L2.5 12.5z"
      fill="currentColor"
    />
  </svg>
);

const CheckIcon = () => (
  <svg
    className="fpm-check"
    viewBox="0 0 16 16"
    width="16"
    height="16"
    aria-hidden="true"
  >
    <path
      d="M3.5 8.2l2.8 2.8 6.2-6.4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Outcome art for one carousel card. */
const PaywallStoryVisual = ({ visual = "photo" }) => {
  if (visual === "pdf" || visual === "word" || visual === "pdf_stack") {
    return (
      <div
        className={`fpm-outcome fpm-outcome--doc fpm-outcome--${visual}`}
        aria-hidden
      >
        {visual === "pdf_stack" ? (
          <>
            <div className="fpm-outcome__page fpm-outcome__page--back" />
            <div className="fpm-outcome__page fpm-outcome__page--mid" />
          </>
        ) : null}
        <div className="fpm-outcome__page">
          <div className="fpm-outcome__page-lines">
            <span />
            <span />
            <span />
            <span />
            <span className="is-short" />
          </div>
          <div className="fpm-outcome__wm">
            <span className="fpm-outcome__mark fpm-outcome__mark--cutout">LS</span>
          </div>
        </div>
      </div>
    );
  }

  if (visual === "tile") {
    return (
      <div className="fpm-outcome fpm-outcome--tile" aria-hidden>
        <div className="fpm-outcome__scene fpm-outcome__scene--tile">
          <div className="fpm-outcome__photo-bg" />
          <div className="fpm-outcome__tile-grid">
            {Array.from({ length: 9 }).map((_, i) => (
              <span
                key={i}
                className="fpm-outcome__mark fpm-outcome__mark--cutout fpm-outcome__mark--mini"
              >
                LS
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (visual === "photo_placed") {
    return (
      <div className="fpm-outcome fpm-outcome--photo" aria-hidden>
        <div className="fpm-outcome__scene">
          <div className="fpm-outcome__photo-bg" />
          <div className="fpm-outcome__placed">
            <span className="fpm-outcome__mark fpm-outcome__mark--cutout">LS</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fpm-outcome fpm-outcome--photo" aria-hidden>
      <div className="fpm-outcome__scene">
        <div className="fpm-outcome__photo-bg" />
        <div className="fpm-outcome__split">
          <div className="fpm-outcome__half fpm-outcome__half--before">
            <span className="fpm-outcome__tag">Before</span>
            <span className="fpm-outcome__mark fpm-outcome__mark--boxed">LS</span>
          </div>
          <div className="fpm-outcome__half fpm-outcome__half--after">
            <span className="fpm-outcome__tag">After</span>
            <span className="fpm-outcome__mark fpm-outcome__mark--cutout">LS</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const DEFAULT_STORY_CARDS = [
  {
    id: "before_after",
    visual: "photo",
    caption: "Before → after cutout",
    chip: "BG Remover",
  },
  {
    id: "on_pdf",
    visual: "pdf",
    caption: "Clean marks on every PDF page",
    chip: "PDF ready",
  },
  {
    id: "on_docs",
    visual: "word",
    caption: "Sharp logos on Word exports",
    chip: "Documents",
  },
];

const DEFAULT_PILL_MS = 6000;

const slideDurationMs = (card) => {
  const ms = card?.durationMs;
  return typeof ms === "number" && ms > 0 ? ms : DEFAULT_PILL_MS;
};

/** Desire cards with auto-advance; each card may set durationMs for its progress pill. */
const PaywallStoryCarousel = ({ cards = DEFAULT_STORY_CARDS, active = true }) => {
  const slides = cards.length ? cards : DEFAULT_STORY_CARDS;
  const slidesKey = slides.map((c) => c.id || c.caption).join("|");
  const [index, setIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);
  const indexRef = useRef(0);
  const elapsedRef = useRef(0);
  const lastTsRef = useRef(null);

  pausedRef.current = paused;
  indexRef.current = index;

  useEffect(() => {
    setIndex(0);
    setProgress(0);
    elapsedRef.current = 0;
    lastTsRef.current = null;
  }, [slidesKey]);

  useEffect(() => {
    if (!active || slides.length < 1) return undefined;
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reduceMotion || slides.length < 2) {
      setProgress(1);
      return undefined;
    }

    let raf = 0;
    const tick = (ts) => {
      if (lastTsRef.current == null) lastTsRef.current = ts;
      const dt = ts - lastTsRef.current;
      lastTsRef.current = ts;

      if (!pausedRef.current) {
        const duration = slideDurationMs(slides[indexRef.current]);
        elapsedRef.current += dt;
        if (elapsedRef.current >= duration) {
          elapsedRef.current = 0;
          setIndex((i) => (i + 1) % slides.length);
          setProgress(0);
        } else {
          setProgress(Math.min(1, elapsedRef.current / duration));
        }
      }
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [active, slides, slidesKey]);

  const goTo = (next) => {
    setIndex(next);
    setProgress(0);
    elapsedRef.current = 0;
    lastTsRef.current = null;
  };

  const current = slides[index] || slides[0];

  return (
    <div
      className={`fpm-carousel${paused ? " is-paused" : ""}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="fpm-carousel__stage">
        {slides.map((card, i) => (
          <div
            key={card.id || i}
            className={`fpm-carousel__slide${i === index ? " is-active" : ""}`}
            aria-hidden={i !== index}
          >
            <PaywallStoryVisual visual={card.visual} />
            {card.chip ? (
              <span className="fpm-outcome__chip">{card.chip}</span>
            ) : null}
          </div>
        ))}
      </div>
      <p className="fpm-story__caption">{current.caption}</p>
      <div className="fpm-carousel__dots" role="tablist" aria-label="Preview slides">
        {slides.map((card, i) => {
          const isActive = i === index;
          return (
            <button
              key={card.id || i}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-label={`Show slide ${i + 1}`}
              className={`fpm-carousel__dot${isActive ? " is-active" : ""}`}
              onClick={() => goTo(i)}
            >
              {isActive ? (
                <span
                  className="fpm-carousel__dot-fill"
                  style={{ transform: `scaleX(${progress})` }}
                />
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Black-theme feature paywall (Canva-style split).
 * Left: plans + benefits + CTA. Right: full-bleed outcome visual.
 */
const FeaturePaywallModal = ({
  open = false,
  featureKey = "logo_bg_remove",
  toolKey = "watermark_maker",
  onClose = () => {},
  onCreditsUpdated = null,
}) => {
  const feature = useMemo(
    () => getPaywallFeature(featureKey, toolKey),
    [featureKey, toolKey]
  );
  const toolLabel =
    PAYWALL_TOOL_LABELS[toolKey] || PAYWALL_TOOL_LABELS.watermark_maker;

  const [selectedPlanId, setSelectedPlanId] = useState(
    feature.recommendedPlanId || "pro"
  );
  const [billing, setBilling] = useState("monthly");
  const [showPricing, setShowPricing] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    setSelectedPlanId(feature.recommendedPlanId || "pro");
    setShowPricing(false);
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, feature.recommendedPlanId, onClose]);

  if (!open) return null;

  const selectedPlan =
    PAYWALL_PLAN_OPTIONS.find((p) => p.id === selectedPlanId) ||
    PAYWALL_PLAN_OPTIONS[1];

  const price =
    billing === "yearly"
      ? yearlyFromMonthly(selectedPlan.monthly).yearlyTotal
      : selectedPlan.monthly;
  const priceLabel =
    billing === "yearly"
      ? `$${Math.round(price)}/yr`
      : `$${selectedPlan.monthly}/mo`;

  const handleUpgrade = () => {
    setShowPricing(true);
  };

  const storyCards = feature.storyCards || DEFAULT_STORY_CARDS;

  const node = (
    <div
      className="fpm-root"
      role="dialog"
      aria-modal="true"
      aria-labelledby="fpm-title"
    >
      <button
        type="button"
        className="fpm-backdrop"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="fpm-panel">
        <button
          type="button"
          className="fpm-close"
          aria-label="Close"
          onClick={onClose}
        >
          ×
        </button>

        <div className="fpm-split">
          <div className="fpm-main">
            <div className="fpm-main__scroll">
              <p className="fpm-eyebrow">
                <CrownIcon />
                {feature.eyebrow}
              </p>
              <h2 id="fpm-title" className="fpm-title">
                Unlock{" "}
                <span className="fpm-title__accent">{feature.featureLabel}</span>
              </h2>
              <p className="fpm-sub">
                You were using <strong>{toolLabel}</strong>.{" "}
                {feature.subhead}
              </p>

              <div className="fpm-billing" role="group" aria-label="Billing">
                <button
                  type="button"
                  className={`fpm-billing__btn${
                    billing === "monthly" ? " is-active" : ""
                  }`}
                  onClick={() => setBilling("monthly")}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  className={`fpm-billing__btn${
                    billing === "yearly" ? " is-active" : ""
                  }`}
                  onClick={() => setBilling("yearly")}
                >
                  Yearly
                  <span className="fpm-billing__save">
                    Save {YEARLY_SAVE_PERCENT}%
                  </span>
                </button>
              </div>

              <div className="fpm-plans" role="radiogroup" aria-label="Plans">
                {PAYWALL_PLAN_OPTIONS.map((plan) => {
                  const active = plan.id === selectedPlanId;
                  const planPrice =
                    billing === "yearly"
                      ? Math.round(yearlyFromMonthly(plan.monthly).yearlyTotal)
                      : plan.monthly;
                  return (
                    <button
                      key={plan.id}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      className={`fpm-plan${active ? " is-active" : ""}`}
                      onClick={() => setSelectedPlanId(plan.id)}
                    >
                      <span
                        className={`fpm-plan__radio${active ? " is-on" : ""}`}
                        aria-hidden
                      />
                      <span className="fpm-plan__body">
                        <span className="fpm-plan__row">
                          <span className="fpm-plan__name">{plan.name}</span>
                          {plan.badge ? (
                            <span className="fpm-plan__badge">{plan.badge}</span>
                          ) : null}
                        </span>
                        <span className="fpm-plan__meta">
                          {plan.audience} · {plan.credits.toLocaleString()}{" "}
                          credits
                        </span>
                      </span>
                      <span className="fpm-plan__price">
                        ${planPrice}
                        <span className="fpm-plan__per">
                          /{billing === "yearly" ? "yr" : "mo"}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="fpm-benefits">
                <h3 className="fpm-benefits__title">
                  Here&apos;s what you get with {selectedPlan.name}:
                </h3>
                <ul className="fpm-benefits__list">
                  {(feature.benefits || []).map((line) => (
                    <li key={line} className="fpm-benefits__item">
                      <CheckIcon />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="fpm-footer">
              <button
                type="button"
                className="fpm-cta"
                onClick={handleUpgrade}
              >
                Upgrade to {selectedPlan.name} — {priceLabel}
              </button>
              <p className="fpm-footnote">
                Opens full checkout. Cancel anytime. Credits follow your plan.
              </p>
              <button
                type="button"
                className="fpm-dismiss"
                onClick={onClose}
              >
                Not now
              </button>
            </div>
          </div>

          {/* Right — auto-scrolling desire cards */}
          <aside className="fpm-story" aria-label="Feature previews">
            <div className="fpm-story__glow" aria-hidden />
            <PaywallStoryCarousel cards={storyCards} active={open && !showPricing} />
          </aside>
        </div>
      </div>

      {showPricing ? (
        <Pricing
          onClose={() => {
            setShowPricing(false);
            onClose();
          }}
          onCreditsUpdated={onCreditsUpdated}
        />
      ) : null}
    </div>
  );

  return createPortal(node, document.body);
};

export default FeaturePaywallModal;
